import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu as MenuIcon, X, Phone } from "lucide-react";
import { useLanguage } from "../i18n";
import { CONTACTS } from "../data/menu";

export const scrollToId = (id) => {
  if (window.__lenis) window.__lenis.scrollTo(`#${id}`, { offset: -70 });
  else document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
};

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { lang, setLang, t } = useLanguage();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const overHero = location.pathname === "/" && !scrolled && !open;
  const links = [
    { key: "nav_home", to: "/", testid: "nav-home-link" },
    { key: "nav_menu", to: "/menu", testid: "nav-menu-link" },
    { key: "nav_gelateria", to: "/gelateria", testid: "nav-gelateria-link" },
    { key: "nav_contacts", to: "/contatti", testid: "nav-contacts-link" },
  ];

  const linkColor = overHero ? "text-cream/85 hover:text-sage" : "text-ink/70 hover:text-terra";
  const activeColor = overHero ? "text-sage" : "text-terra";

  return (
    <header
      data-testid="main-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ${
        scrolled || open ? "bg-paper/90 backdrop-blur-md border-b border-line" : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-[70px] flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-3">
          <img src="/logo.png" alt="Da Polin" className="h-11 w-11 rounded-full shadow-sm" />
          <span className={`hidden sm:block font-serif text-xl tracking-wide transition-colors duration-500 ${overHero ? "text-cream" : "text-ink"}`}>
            Da <span className={`italic ${overHero ? "text-sage" : "text-terra"}`}>Polin</span>
          </span>
        </Link>
        <nav className="hidden md:flex items-center gap-8">
          {links.map((l) => (
            <Link
              key={l.key}
              to={l.to}
              data-testid={l.testid}
              className={`text-sm tracking-[0.18em] uppercase transition-colors duration-300 ${linkColor} ${location.pathname === l.to ? activeColor : ""}`}
            >
              {t(l.key)}
            </Link>
          ))}
        </nav>
        <div className="flex items-center gap-4">
          <div data-testid="language-toggle" className="hidden sm:flex items-center text-xs tracking-[0.2em] font-accent">
            <button
              data-testid="lang-it-button"
              onClick={() => setLang("it")}
              className={`px-2 py-1 transition-colors duration-300 ${lang === "it" ? activeColor : overHero ? "text-cream/50 hover:text-cream" : "text-ink/40 hover:text-ink"}`}
            >
              IT
            </button>
            <span className={overHero ? "text-cream/30" : "text-ink/25"}>/</span>
            <button
              data-testid="lang-en-button"
              onClick={() => setLang("en")}
              className={`px-2 py-1 transition-colors duration-300 ${lang === "en" ? activeColor : overHero ? "text-cream/50 hover:text-cream" : "text-ink/40 hover:text-ink"}`}
            >
              EN
            </button>
          </div>
          <a
            href={CONTACTS.phoneHref}
            data-testid="nav-call-button"
            className={`hidden sm:inline-flex items-center gap-2 rounded-full border px-5 py-2 text-xs tracking-[0.18em] uppercase transition-colors duration-300 ${
              overHero ? "border-cream/50 text-cream hover:bg-cream hover:text-ink" : "border-terra/60 text-terra hover:bg-terra hover:text-cream"
            }`}
          >
            <Phone size={13} /> {t("nav_call")}
          </a>
          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen(!open)}
            aria-label="Menu"
            className={`md:hidden transition-colors duration-300 ${overHero ? "text-cream" : "text-ink"}`}
          >
            {open ? <X size={22} /> : <MenuIcon size={22} />}
          </button>
        </div>
      </div>
      {open && (
        <nav data-testid="mobile-nav" className="md:hidden bg-paper border-b border-line px-6 py-6 flex flex-col gap-5">
          {links.map((l) => (
            <Link
              key={l.key}
              to={l.to}
              data-testid={`mobile-${l.testid}`}
              className={`text-sm tracking-[0.18em] uppercase transition-colors duration-300 ${location.pathname === l.to ? "text-terra" : "text-ink/70 hover:text-terra"}`}
            >
              {t(l.key)}
            </Link>
          ))}
          <div className="flex items-center gap-1 text-xs tracking-[0.2em] font-accent pt-2 border-t border-line">
            <button data-testid="mobile-lang-it-button" onClick={() => setLang("it")} className={`px-2 py-1 ${lang === "it" ? "text-terra" : "text-ink/40"}`}>IT</button>
            <span className="text-ink/25">/</span>
            <button data-testid="mobile-lang-en-button" onClick={() => setLang("en")} className={`px-2 py-1 ${lang === "en" ? "text-terra" : "text-ink/40"}`}>EN</button>
          </div>
        </nav>
      )}
    </header>
  );
};
